import { type ComponentProps, type ReactNode, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  Flex,
  Text,
  type TextProps,
} from '@/ui';
import { ConfirmDialogTitle } from './ConfirmDialogTitle';

type Props = {
  children?: ReactNode;
  title: ReactNode;
  message?: string;
  messageProps?: TextProps;
  isOpen: boolean;
  isLoading?: boolean;
  /** Holds the confirm action, e.g. while a check the user should see first is still running. */
  isConfirmDisabled?: boolean;
  cancelButtonText?: string | null;
  confirmButtonText?: string;
  dialogOverlayProps?: ComponentProps<typeof AlertDialogOverlay>;
  onConfirm: () => void;
  onCancel: () => void;
};

export const ConfirmDialog = ({
  children,
  title,
  message,
  messageProps,
  isOpen,
  isLoading,
  isConfirmDisabled = false,
  cancelButtonText = 'Cancel',
  confirmButtonText = 'Confirm',
  dialogOverlayProps,
  onConfirm,
  onCancel,
}: Props) => {
  const cancelRef = useRef(null);

  return (
    <AlertDialog
      data-testid="confirm-dialog"
      isCentered
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      // `returnFocusOnClose`` is disabled because we can't guarantee that the element that
      // opened the modal will still be focusable when the modal closes. For example, this
      // modal can be opened from inside a `Menu` component on the agenda view and the menu
      // closes as soon as the menu item is clicked. Chakra is unable to return focus to the
      // menu item and the scroll position of the page is reset to the top.
      // https://github.com/chakra-ui/chakra-ui/issues/7734
      returnFocusOnClose={false}
      onClose={onCancel}
    >
      <AlertDialogOverlay {...dialogOverlayProps}>
        <AlertDialogContent
          borderRadius="20"
          minW={{ sm: '450px' }}
          mx={{ base: 4, sm: 0 }}
        >
          <AlertDialogBody
            px={{ base: 4, sm: '50px' }}
            py="10"
            textAlign="center"
          >
            {typeof title === 'string' ? (
              <ConfirmDialogTitle>{title}</ConfirmDialogTitle>
            ) : (
              title
            )}

            {!!message && (
              <Text color="customgray.mid" mt="2" {...messageProps}>
                {message}
              </Text>
            )}
            {children}
            <Flex gap="3" justify="center" mt="8">
              {cancelButtonText && (
                <Button
                  isDisabled={isLoading}
                  ref={cancelRef}
                  variant="secondary"
                  onClick={onCancel}
                >
                  {cancelButtonText}
                </Button>
              )}
              <Button
                colorScheme="red"
                isDisabled={isConfirmDisabled}
                isLoading={isLoading}
                onClick={onConfirm}
              >
                {confirmButtonText}
              </Button>
            </Flex>
          </AlertDialogBody>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
